import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'rs_superadmin_highlight_stores_v1';

export function normalizeHighlightStore(s) {
  if (!s || s.id == null) return null;
  return {
    id: s.id,
    store_id: s.store_id != null ? String(s.store_id) : '',
    name: s.name || '',
    wh_client_id: s.wh_client_id != null ? String(s.wh_client_id).trim() : '',
  };
}

const defaultValue = {
  isSuperuserHighlight: false,
  highlightedStores: [],
  setHighlightedStores: () => {},
  isOrderRowHighlighted: () => false,
  isBillingExpenseRowHighlighted: () => false,
  isSellerIdHighlighted: () => false,
  isWarehouseReturnRowHighlighted: () => false,
};

const HighlightedStoresContext = createContext(defaultValue);

export function HighlightedStoresProvider({ children }) {
  const { user } = useAuth();
  const isSuperuser = !!user?.is_superuser;

  const [highlightedStores, setHighlightedStoresState] = useState([]);

  useEffect(() => {
    if (!isSuperuser) {
      setHighlightedStoresState([]);
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const cleaned = parsed.map(normalizeHighlightStore).filter(Boolean);
      setHighlightedStoresState(cleaned);
    } catch {
      /* ignore */
    }
  }, [isSuperuser]);

  const setHighlightedStores = useCallback((next) => {
    if (!isSuperuser) return;
    setHighlightedStoresState((prev) => {
      const list = (typeof next === 'function' ? next(prev) : next)
        .map(normalizeHighlightStore)
        .filter(Boolean);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch {
        /* ignore */
      }
      return list;
    });
  }, [isSuperuser]);

  const value = useMemo(() => {
    if (!isSuperuser) {
      return { ...defaultValue };
    }

    const sellerIds = new Set(
      highlightedStores.map((s) => String(s.store_id || '').trim()).filter(Boolean),
    );
    const storePks = new Set(
      highlightedStores.map((s) => Number(s.id)).filter((n) => !Number.isNaN(n)),
    );
    const whClientIds = new Set(
      highlightedStores.map((s) => String(s.wh_client_id || '').trim()).filter(Boolean),
    );

    return {
      isSuperuserHighlight: true,
      highlightedStores,
      setHighlightedStores,
      isOrderRowHighlighted: (order) =>
        order?.seller_id != null && sellerIds.has(String(order.seller_id).trim()),
      isBillingExpenseRowHighlighted: (exp) =>
        exp?.store != null && storePks.has(Number(exp.store)),
      isSellerIdHighlighted: (sellerId) =>
        sellerId != null && sellerIds.has(String(sellerId).trim()),
      isWarehouseReturnRowHighlighted: (ret) => {
        const cid =
          ret?.client_id ??
          ret?.client?.id ??
          ret?.clientId ??
          ret?.wh_client_id;
        if (cid == null || cid === '') return false;
        return whClientIds.has(String(cid).trim());
      },
    };
  }, [isSuperuser, highlightedStores, setHighlightedStores]);

  return (
    <HighlightedStoresContext.Provider value={value}>
      {children}
    </HighlightedStoresContext.Provider>
  );
}

export function useHighlightedStores() {
  return useContext(HighlightedStoresContext);
}
